import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { UserCog } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only modal for reassigning the `created_by` of a checklist or test
 * report. Used when an employee leaves and their drafts must be moved to a
 * still-active user without breaking the audit trail.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange(boolean)
 *  - instance: { id, created_by, created_by_name, ...display fields }
 *  - kind: 'checklist' | 'test-report'  (decides API path)
 *  - onReassigned(newOwnerId, newOwnerName)  callback after success
 */
export default function ReassignOwnerDialog({ open, onOpenChange, instance, kind, onReassigned }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [pickedId, setPickedId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUsers(r.data.filter(u => u.is_approved && u.is_active !== false)))
      .catch(() => toast.error(t('auto.iso.failedToLoadUsersAdminOnly')));
    setPickedId(instance?.created_by || '');
  }, [open, instance]);

  const submit = async () => {
    if (!pickedId || pickedId === instance?.created_by) {
      toast.info(t('auto.iso.pickADifferentUser'));
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const path = kind === 'checklist'
        ? `${API}/checklists/instances/${instance.id}/reassign`
        : `${API}/test-reports/instances/${instance.id}/reassign`;
      const r = await axios.post(path, { new_owner_id: pickedId },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reassigned to ${r.data.new_owner_name}`);
      onReassigned?.(pickedId, r.data.new_owner_name);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reassign-owner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" /> {t('auto.iso.reassignOwner2')}
          </DialogTitle>
          <DialogDescription>
            Move ownership of this {kind === 'checklist' ? 'checklist' : 'test report'} to another user. The original owner is recorded for audit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-2 border">
            <div><span className="font-medium text-gray-700">{t('auto.iso.document2')}</span> {instance?.report_number || instance?.checklist_number || instance?.id?.slice(0, 8)}</div>
            <div><span className="font-medium text-gray-700">{t('auto.iso.currentOwner')}</span> {instance?.created_by_name || '—'}</div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">{t('auto.iso.newOwner')}</label>
            <Select value={pickedId} onValueChange={setPickedId}>
              <SelectTrigger className="w-full" data-testid="reassign-user-select">
                <SelectValue placeholder={t('auto.iso.pickAUser')} />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.full_name || u.username} <span className="text-xs text-gray-400">({u.role})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="dialog-cancel-btn">{t('auto.iso.cancel')}</Button>
          <Button onClick={submit} disabled={submitting || !pickedId || pickedId === instance?.created_by} data-testid="reassign-submit-btn">
            {submitting ? t('auto.iso.reassigning') : t('auto.iso.reassign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
